import React, { FC, useCallback, useEffect, useState } from "react";

import {
  ActionValue,
  basketActiveAtom,
  basketSelector,
  basketTotalCountSelector,
  DataViewKey,
  dataViewPage,
  KpkBasket,
  SentryBreadcrumbCategory,
  useBasketAddTo,
  useBasketCreate,
  useBasketDelete,
  useBasketEdit,
  useBasketList,
  useBasketPageCount,
  useBasketSynchronization,
} from "@keepeek/commons";
import {
  BaseComponentProps,
  BasketAddTo,
  BasketCardBackgroundSize,
  BasketCreate,
  BasketDelete,
  BasketEdit,
  BasketLayout,
  withSuspense,
} from "@keepeek/refront-components";
import { Box, Typography, useTheme } from "@mui/material";
import * as Sentry from "@sentry/nextjs";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";

import { KIcon } from "../../components/common/KIcon";
import { CONFIGURATION_SECTION_PATH } from "../../components/layout/adminMenu";
import { focusSelector } from "../../lib/focusSelector";
import { Basket as BasketConfiguration } from "../../models/configuration/components/basket";
import { currentBasketIdState } from "../../providers/basket/atoms";
import { useConfiguration } from "../../providers/config/hooks";
import { configSectionComponentBasketSelector } from "../../providers/config/selectors";
import { useElement } from "../../providers/element/hooks";
import { PagesPath } from "../App/utils";
import BasketAdvancedLoading from "./AdvancedBasketLoading";
import BasketActions from "./BasketActions";
import BasketElements from "./BasketElements";
import BasketError from "./BasketError";
import BasketShare from "./BasketShare";

export type BasketProps = {} & BaseComponentProps;

const Basket: FC<React.PropsWithChildren<BasketProps>> = function () {
  const { t } = useTranslation();
  const theme = useTheme();
  const [activeBasket, setActiveBasket] = useRecoilState(basketActiveAtom);
  const [currentBasketId, setCurrentBasketId] = useRecoilState(currentBasketIdState);
  const currentBasket = useRecoilValue(basketSelector({ id: currentBasketId }));
  useBasketSynchronization(currentBasket?.id);
  const { activateBasketPartitioning } = useRecoilValue(configSectionComponentBasketSelector) ?? {
    activateBasketPartitioning: false,
  };
  const { basketList } = useBasketList({ basketPartitioning: activateBasketPartitioning });
  const [page, setPage] = useRecoilState(dataViewPage(DataViewKey.BASKET));
  const { element } = useElement();
  const { enqueueSnackbar } = useSnackbar();

  const sentryCallback = ({ category, message }) => Sentry.addBreadcrumb({ category, message });
  const snackbarCallback = ({ message, options }) => enqueueSnackbar(message, options);

  const selectCurrentBasketId = (basketId: KpkBasket["id"]) => {
    setCurrentBasketId(basketId);
    setPage(1);
    Sentry.addBreadcrumb({
      category: SentryBreadcrumbCategory.USER_INTERACTION,
      message: `selectCurrentBasketId: ${basketId}`,
    });
  };
  const totalCount = useRecoilValue(basketTotalCountSelector(currentBasket?.id));
  const router = useRouter();
  const pageCount = useBasketPageCount(currentBasketId);
  const {
    open,
    close,
    count,
    basketList: basketListAddTo,
    onSelectBasket,
    createBasket: createBasketAddTo,
  } = useBasketAddTo(
    element,
    currentBasket?.id,
    activateBasketPartitioning,
    sentryCallback,
    snackbarCallback,
  );
  const [openDelete, setOpenDelete] = useState(false);

  const { deleteBasket } = useBasketDelete(
    currentBasket?.id,
    sentryCallback,
    snackbarCallback,
    () => {
      setOpenDelete(false);
      setCurrentBasketId(basketList.length > 0 ? basketList?.[0]?.id : undefined);
    },
  );
  const {
    open: openCreate,
    title,
    setTitle,
    close: closeCreate,
    createBasket,
    loading,
  } = useBasketCreate(
    element,
    activateBasketPartitioning,
    sentryCallback,
    snackbarCallback,
    (basketId) => {
      setCurrentBasketId(basketId);
    },
  );

  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [titleEdit, setTitleEdit] = useState<string>("");
  const { update } = useBasketEdit({
    basketId: currentBasket?.id,
    sentryCallback,
    snackbarCallback,
    updateCallback: () => setOpenEdit(false),
  });

  const keyboardNavigation = useCallback(
    (evt: KeyboardEvent) => {
      const isFocused = document?.getElementById("Basket")?.contains(document.activeElement);
      evt = evt || window.event;
      if (isFocused) {
        if (evt.key === "Escape") {
          const focusable = focusSelector(document.getElementById("main"));
          focusable?.focus();
          setActiveBasket((prevState) => !prevState);
        }
      }
    },
    [setActiveBasket],
  );

  // Keyboard A11Y
  useEffect(() => {
    if (activeBasket) {
      document.addEventListener("keydown", keyboardNavigation, false);
    } else {
      setPage(1);
    }

    return () => {
      document.removeEventListener("keydown", keyboardNavigation, false);
    };
  }, [activeBasket, keyboardNavigation, setPage]);

  const handleTriggeredAction = (action: ActionValue) => {
    switch (action) {
      case ActionValue.DELETE_BASKET:
        setOpenDelete(true);
        break;
      case ActionValue.EDIT_BASKET:
        setTitleEdit(currentBasket?.title ?? "");
        setOpenEdit(true);
      default:
        break;
    }
  };

  const { elementImageSize } = useConfiguration<BasketConfiguration>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_BASKET,
  ) ?? { elementImageSize: undefined };

  return (
    <>
      <BasketLayout
        currentBasket={currentBasket}
        basketList={basketList}
        elementCount={totalCount}
        currentPage={page}
        pageCount={pageCount}
        active={activeBasket}
        handlePageChange={setPage}
        handleBasketSelect={selectCurrentBasketId}
        handleBasketsRouting={() => router.push(PagesPath.BASKETS)}
        actions={<BasketActions triggeredAction={handleTriggeredAction} />}
        KIcon={KIcon}
        emptyState={
          <Box
            mt={3}
            style={{
              color: theme.palette.grey[500],
            }}
          >
            <Typography variant="h6" align="center">
              {t("basket.layout.empty-state-basket.label")}
            </Typography>
          </Box>
        }
        childrenEmpty={
          <Box
            mt={3}
            style={{
              color: theme.palette.grey[500],
            }}
          >
            <Typography variant="h6" align="center">
              {t("basket.layout.empty-basket.label")}
            </Typography>
          </Box>
        }
      >
        <BasketElements sentryCallback={sentryCallback} />
      </BasketLayout>
      <BasketCreate
        open={openCreate}
        createBasket={createBasket}
        close={closeCreate}
        title={title}
        setTitle={setTitle}
        loading={loading}
      />
      <BasketAddTo
        open={open}
        close={close}
        basketList={basketListAddTo}
        count={count}
        onSelectBasket={onSelectBasket}
        createBasket={createBasketAddTo}
        backgroundSize={
          elementImageSize ? BasketCardBackgroundSize.Contain : BasketCardBackgroundSize.Cover
        }
        KIcon={KIcon}
      />
      {currentBasket && (
        <>
          <BasketDelete
            open={openDelete}
            close={() => setOpenDelete(false)}
            currentBasketTitle={currentBasket.title}
            deleteBasket={deleteBasket}
            numberOfBaskets={1}
          />
          <BasketEdit
            title={titleEdit}
            setTitle={setTitleEdit}
            update={() => update({ title: titleEdit })}
            open={openEdit}
            close={() => setOpenEdit(false)}
          />
          <BasketShare
            basketId={currentBasket.id}
            basketTitle={currentBasket.title}
            sentryCallback={sentryCallback}
            snackbarCallback={snackbarCallback}
          />
        </>
      )}
    </>
  );
};

export default withSuspense(Basket, {
  CustomErrorComponent: BasketError,
  LoadingComponent: BasketAdvancedLoading,
});
